<template>
  <div class="additional-information-page">
    <div class="page-container">
      <!-- 表单内容区域 - 使用 dynamic-form 组件 -->
      <dynamic-form ref="dynamicFormRef" :config="formConfig" :validation-mode="'format-only'"
        :show-submit-button="false" @submit="handleSubmit" />

      <!-- 自定义提交按钮 - 自己控制 loading 状态 -->
      <div class="form-actions">
        <on-button @click="handleSubmitClick" :loading="submitting" :disabled="submitting">
          {{ $t('message.additionalInfo.submit') }}
        </on-button>
      </div>

      <!-- 返回商户 -->
      <div v-if="returnUrl" class="btn-cancel-box">
        <span class="btn-cancel" @click="handleReturnMerchant">
          <img src="@/assets/images/back.png" alt="" class="back">
          <span class="btn-cancel-text">{{ $t('message.checkout.returnMerchant') }}</span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * 额外信息收集页面
 * 用于支付流程中收集第三方所需的额外字段
 * @author Nemo
 */
import DynamicForm from '@/componentsV2/busi/dynamic-form';
import OnButton from '@/componentsV2/base/button/index.vue';
import { submitAdditionalInfo } from '@/api/common';

export default {
  name: 'AdditionalInformation',

  components: {
    DynamicForm,
    OnButton,
  },

  data() {
    return {
      // 表单字段配置
      formConfig: [
        {
          name: 'cvv',
          label: 'message.checkout.cvv',
          renderComponent: 'BaseInput',
          placeholder: 'message.checkout.cvv',
          maxLength: 4,
          display: 'Y',
          require: 'Y',
          validationRules: ['cvv'],
          inputHandler: 'cvvInputHandler',
        },
        {
          name: 'firstName',
          label: 'message.checkout.firstName',
          renderComponent: 'BaseInput',
          placeholder: 'message.checkout.firstName',
          maxLength: 64,
          display: 'Y',
          require: 'Y',
          validationRules: ['required'],
        },
        {
          name: 'lastName',
          label: 'message.checkout.lastName',
          renderComponent: 'BaseInput',
          placeholder: 'message.checkout.lastName',
          maxLength: 64,
          display: 'Y',
          require: 'Y',
          validationRules: ['required'],
        },
      ],

      // 跳转 URL（从URL参数获取）
      returnUrl: '',

      // 提交状态
      submitting: false,

      // 交易缓存key
      key: '',
    };
  },

  mounted() {
    this.initPage();
  },

  methods: {
    /**
     * 初始化页面
     */
    initPage() {
      // 从 URL 参数获取跳转地址
      const urlParams = new URLSearchParams(window.location.search);

      // 解析 returnUrl 参数
      this.returnUrl = urlParams.get('returnUrl') || urlParams.get('redirect_url') || '';

      // 解析 key 参数
      this.key = urlParams.get('key') || '';

      // 解析 name 参数并回显表单数据
      const nameParam = urlParams.get('name');

      if (nameParam) {
        try {
          // URL decode 并解析 JSON
          const formData = JSON.parse(decodeURIComponent(nameParam));

          // 使用 $nextTick 确保 dynamic-form 组件已经挂载
          this.$nextTick(() => {
            if (this.$refs.dynamicFormRef) {
              // 回显表单数据
              this.$refs.dynamicFormRef.setFormData(formData);
            }
          });
        } catch (error) {
          // 解析失败，忽略
        }
      }
    },

    /**
     * CVV 输入处理 - 只允许输入数字
     * @param {Event} event - 输入事件对象
     */
    cvvInputHandler(event) {
      // 过滤掉所有非数字字符
      const value = event.target.value.replace(/[^\d]/g, '');
      event.target.value = value;
    },

    /**
     * 拼接URL参数
     * @param {String} url - 基础URL
     * @param {String} status - 状态
     * @param {String} respMsg - 响应消息
     * @param {String} respCode - 响应码
     * @returns {String} 拼接后的URL
     */
    appendUrlParams(url, status, respMsg, respCode) {
      if (!url) return url;
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}status=${encodeURIComponent(status || '')}&respMsg=${encodeURIComponent(respMsg || '')}&respCode=${encodeURIComponent(respCode || '')}`;
    },

    /**
     * 点击提交按钮 - 触发 dynamic-form 的验证和提交
     */
    async handleSubmitClick() {
      // 调用 dynamic-form 的提交方法
      await this.$refs.dynamicFormRef.submit();
    },

    /**
     * 返回商户 - 只拼接 status=N 并跳转回 returnUrl
     */
    handleReturnMerchant() {
      if (!this.returnUrl) {
        return;
      }

      // 只拼接 status 参数
      const separator = this.returnUrl.includes('?') ? '&' : '?';
      const returnUrl = `${this.returnUrl}${separator}status=N`;
      window.location.href = returnUrl;
    },

    /**
     * 处理表单提交（由 dynamic-form 验证通过后触发）
     * @param {Object} formData - dynamic-form 验证通过后返回的表单数据
     */
    async handleSubmit(formData) {
      // 防止重复提交
      if (this.submitting) {
        return;
      }

      // 校验必填参数
      if (!this.key) {
        return;
      }

      this.submitting = true;

      try {
        // 调用后端接口提交数据
        const response = await submitAdditionalInfo({
          key: this.key,
          cvv: formData.cvv,
          firstName: formData.firstName,
          lastName: formData.lastName,
        });

        // 处理响应
        if (response.respCode === '20000' && response.data?.status === 'R') {
          // 重定向状态 - 跳转到接口返回的 redirectUrl
          if (response.data.redirectUrl) {
            // 跳转前保持按钮 disabled，防止二次提交
            const redirectUrl = this.appendUrlParams(
              response.data.redirectUrl,
              response.data?.status || 'F',
              response.respMsg,
              response.respCode
            );
            window.location.href = redirectUrl;
            return;
          }
        }

        // 其他情况 - 跳转回 returnUrl
        if (this.returnUrl) {
          // 跳转前保持按钮 disabled，防止二次提交
          const returnUrl = this.appendUrlParams(
            this.returnUrl,
            response.data?.status || 'F',
            response.respMsg,
            response.respCode
          );
          window.location.href = returnUrl;
          return;
        }

      } catch (error) {
        // 异常情况 - 跳转回 returnUrl
        if (this.returnUrl) {
          // 跳转前保持按钮 disabled，防止二次提交
          const returnUrl = this.appendUrlParams(
            this.returnUrl,
            'F',
            error?.respMsg || error?.message || 'Unknown error',
            error?.respCode || ''
          );
          window.location.href = returnUrl;
          return;
        }
      }

      // 注意：这里不需要 finally 块，因为所有情况都会跳转
      // 如果没有跳转（没有 returnUrl），才恢复按钮状态
      this.submitting = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.additional-information-page {
  min-height: 100vh;
  background-color: #F5F5F5;
  padding: 40px 20px;
  box-sizing: border-box;

  .page-container {
    max-width: 600px;
    margin: 0 auto;
    background-color: #FFFFFF;
    border-radius: 18px;
    padding: 40px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

// 移动端适配
@media (max-width: 768px) {
  .additional-information-page {
    padding: 16px;

    .page-container {
      padding: 24px 16px;
      border-radius: 12px;
    }

    .form-actions {
      margin-top: 16px;
    }
  }
}

.form-actions {
  margin-top: 24px;
  display: flex;
  justify-content: center;
}

.btn-cancel-box {
  margin-top: 16px;
  display: flex;
  justify-content: center;

  .btn-cancel {
    display: flex;
    align-items: center;
    cursor: pointer;
    color: #666;
    transition: color 0.3s;

    &:hover {
      color: #333;
    }

    .back {
      width: 16px;
      height: 16px;
      margin-right: 6px;
    }

    .btn-cancel-text {
      font-size: 14px;
    }
  }
}
</style>
